import posthog from "posthog-js";
import React, { FC, useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, IconButton, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import { DateTimePicker } from "@mui/x-date-pickers";

import HelpInfo from "../../components/HelpInfo";
import SaveButton from "../../components/SaveButton";
import SettingSection from "../../components/SettingSection";
import TextField from "../../components/styled/TextField";
import { DialogController } from "../../hooks/use-dialog";
import { useSelector } from "../../store";
import {
  ServiceAccountProperties,
  useCreateServiceAccountMutation,
} from "../../store/pomeriumApi";
import { DisplayJWT } from "../settings/components/DisplayJWT";

type Props = {
  dialog: DialogController<never>;
};

const AddServiceAccount: FC<Props> = ({ dialog }) => {
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const [description, setDescription] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const [token, setToken] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<Date | null | undefined>(
    undefined,
  );
  const [dateError, setDateError] = useState<string | null>("");
  const [createServiceAccount] = useCreateServiceAccountMutation();

  useEffect(() => {
    if (!dialog.open) {
      setDescription("");
      setUserId("");
      setExpiresAt(undefined);
      setToken("");
    }
  }, [dialog.open]);
  const handleSave = async () => {
    const properties: ServiceAccountProperties = {
      description,
      userId,
    };
    if (expiresAt) {
      properties["expiresAt"] = expiresAt?.toISOString();
    }
    const update = await createServiceAccount({
      organizationId,
      clusterId,
      serviceAccountProperties: properties,
    }).unwrap();
    if (update?.token) {
      posthog.capture("new_service_account_created");
      setToken(update?.token);
    }
  };

  if (token) {
    return (
      <DisplayJWT
        open={dialog.open}
        jwt={token}
        onClose={() => dialog.handleClose()}
        title={
          <>
            Service account token <HelpInfo docsKey="service-account-token" />
          </>
        }
        message="Make sure to copy your service account token and save it in a safe place:"
      />
    );
  }

  return (
    <Dialog
      open={dialog.open}
      onClose={dialog.handleClose}
      scroll="paper"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        New service account
        <IconButton
          color="primary"
          onClick={() => dialog.handleClose()}
          size="large"
          sx={{ ml: "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Container disableGutters>
          <Stack>
            <SettingSection
              label="Description"
              docsKey="service-account-description"
            >
              <TextField
                error={!description}
                fullWidth
                value={description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDescription(e.target.value)
                }
              />
            </SettingSection>
            <SettingSection
              label="Expires"
              docsKey="service-account-expiration"
            >
              <DateTimePicker
                value={expiresAt}
                onChange={(newValue) => setExpiresAt(newValue)}
                onError={(error) => setDateError(error)}
                slotProps={{
                  textField: {
                    error: expiresAt ? !!dateError : false,
                  },
                }}
              />
            </SettingSection>
            <SettingSection label="User ID" docsKey="service-account-user-id">
              <TextField
                error={!userId}
                fullWidth
                value={userId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setUserId(e.target.value)
                }
              />
            </SettingSection>
          </Stack>
        </Container>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => dialog.handleClose()}>Cancel</Button>
        <SaveButton
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={!description || !userId}
        >
          Save
        </SaveButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddServiceAccount;
